import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Slider,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import { MediaPlayerProps, ViewerError } from '../../../types/viewer.types';
import { useDirectContent } from '../../../hooks/useDirectContent';

/**
 * Media Player component for video and audio files with streaming support
 *
 * Supported Audio Formats:
 * - MP3 (audio/mpeg) - Universal browser support
 * - WAV (audio/wav, audio/wave, audio/x-wav) - Universal browser support
 * - OGG/Opus (audio/ogg, audio/opus) - Chrome, Firefox, Edge
 * - M4A/AAC (audio/mp4, audio/aac) - Most modern browsers
 * - FLAC (audio/flac) - Chrome, Firefox, Edge (not Safari)
 * - WebM Audio (audio/webm) - Chrome, Firefox, Edge
 *
 * Limited/No Browser Support (may require conversion):
 * - WMA (audio/x-ms-wma) - No native browser support
 * - ALAC (audio/alac) - Limited support
 *
 * Note: The HTML5 audio element handles format compatibility automatically.
 * Unsupported formats will trigger an error that can prompt conversion.
 */
const MediaPlayer: React.FC<MediaPlayerProps> = ({
  contentId,
  metadata,
  onError,
  onLoad,
  onTimeUpdate,
  onVolumeChange,
  autoPlay = false,
  controls = true,
  muted = false,
  loop = false,
  poster,
  className,
  style,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(muted);
  const [showCustomControls, setShowCustomControls] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  
  console.log('MediaPlayer initialized with:', { contentId, metadata, autoPlay, controls, muted, showCustomControls });
  
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeUpdateTimeoutRef = useRef<NodeJS.Timeout>();

  const { contentUrl, isDirectDownload, downloadMethod } = useDirectContent(contentId, undefined, metadata);
  console.log('MediaPlayer: contentUrl from hook:', contentUrl, 'isDirectDownload:', isDirectDownload, 'downloadMethod:', downloadMethod);

  const isVideo = metadata.contentType === 'video';

  // Initialize media element only when contentUrl changes
  useEffect(() => {
    const media = mediaRef.current;
    if (!media || !contentUrl) {
      console.log('Media element or contentUrl not ready', { media: !!media, contentUrl });
      return;
    }

    console.log('Initializing media element with contentUrl:', contentUrl, 'isDirectDownload:', isDirectDownload);
    setLoading(true);

    // Set media source
    media.src = contentUrl;
    
    // Set preload and buffering hints for smooth playback
    media.preload = 'auto'; // Preload as much as possible
    
    // For video elements, set additional attributes
    if (media instanceof HTMLVideoElement) {
      media.playsInline = true; // Better mobile support
    }

    // Media event handlers
    const handleLoadStart = () => {
      console.log('Media load started for URL:', contentUrl);
      setLoading(true);
    };

    const handleLoadedMetadata = () => {
      console.log('Media metadata loaded:', {
        duration: media.duration,
        videoWidth: (media as HTMLVideoElement).videoWidth,
        videoHeight: (media as HTMLVideoElement).videoHeight,
      });
      setDuration(media.duration || 0);
      setLoading(false);
      onLoad?.();
    };

    const handleLoadedData = () => {
      console.log('Media data loaded');
      setLoading(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleTimeUpdate = () => {
      const time = media.currentTime || 0;
      setCurrentTime(time);
      onTimeUpdate?.(time, media.duration || 0);
    };

    const handleVolumeChange = () => {
      const newVolume = media.volume;
      const newMuted = media.muted;
      setVolume(newVolume);
      setIsMuted(newMuted);
      onVolumeChange?.(newVolume, newMuted);
    };

    const handleError = (event: Event) => {
      console.error('Media error:', event);
      const error = (media as any).error;
      const viewerError: ViewerError = {
        code: 'STREAMING_ERROR',
        message: `Failed to load ${isVideo ? 'video' : 'audio'}: ${error?.message || 'Unknown error'}`,
        details: error,
        retryable: true,
      };
      setLoading(false);
      onError?.(viewerError);
    };

    const handleCanPlay = () => {
      setLoading(false);
      console.log('Media can play');
      // Debug: Check if controls attribute is actually set
      if (media instanceof HTMLVideoElement || media instanceof HTMLAudioElement) {
        console.log('MediaPlayer: controls attribute =', media.controls);
        console.log('MediaPlayer: element type =', media instanceof HTMLAudioElement ? 'AUDIO' : 'VIDEO');
        console.log('MediaPlayer: element visibility =', {
          display: window.getComputedStyle(media).display,
          visibility: window.getComputedStyle(media).visibility,
          opacity: window.getComputedStyle(media).opacity,
          width: window.getComputedStyle(media).width,
          height: window.getComputedStyle(media).height,
        });
        if (media instanceof HTMLVideoElement) {
          console.log('MediaPlayer: video dimensions =', {
            videoWidth: media.videoWidth,
            videoHeight: media.videoHeight,
            clientWidth: media.clientWidth,
            clientHeight: media.clientHeight
          });
        } else {
          console.log('MediaPlayer: audio element dimensions =', {
            clientWidth: media.clientWidth,
            clientHeight: media.clientHeight,
            offsetWidth: media.offsetWidth,
            offsetHeight: media.offsetHeight,
            scrollWidth: media.scrollWidth,
            scrollHeight: media.scrollHeight,
          });
        }
      }
    };

    const handleProgress = () => {
      const buffered = media.buffered;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        const duration = media.duration;
        if (duration > 0) {
          const bufferedPercentage = (bufferedEnd / duration) * 100;
          console.log(`Buffered: ${bufferedPercentage.toFixed(2)}% (${bufferedEnd.toFixed(2)}s of ${duration.toFixed(2)}s)`);
        }
      }
    };

    const handleWaiting = () => {
      console.log('Media waiting for data...');
    };

    const handleStalled = () => {
      console.log('Media stalled - network issues or slow connection');
    };

    // Add event listeners
    media.addEventListener('loadstart', handleLoadStart);
    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('loadeddata', handleLoadedData);
    media.addEventListener('canplay', handleCanPlay);
    media.addEventListener('play', handlePlay);
    media.addEventListener('pause', handlePause);
    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('volumechange', handleVolumeChange);
    media.addEventListener('error', handleError);
    media.addEventListener('progress', handleProgress);
    media.addEventListener('waiting', handleWaiting);
    media.addEventListener('stalled', handleStalled);

    return () => {
      media.removeEventListener('loadstart', handleLoadStart);
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('loadeddata', handleLoadedData);
      media.removeEventListener('canplay', handleCanPlay);
      media.removeEventListener('play', handlePlay);
      media.removeEventListener('pause', handlePause);
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('volumechange', handleVolumeChange);
      media.removeEventListener('error', handleError);
      media.removeEventListener('progress', handleProgress);
      media.removeEventListener('waiting', handleWaiting);
      media.removeEventListener('stalled', handleStalled);
    };
  }, [contentUrl, isDirectDownload]); // Only re-run when contentUrl changes

  // Set media attributes separately to avoid re-renders
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    
    media.autoplay = autoPlay;
    media.muted = muted;
    media.loop = loop;
    media.volume = volume;
  }, [autoPlay, muted, loop, volume]);

  // Format time for display
  const formatTime = useCallback((seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }, []);

  // Playback controls
  const handlePlayPause = useCallback(() => {
    const media = mediaRef.current;
    if (!media) {
      console.log('MediaPlayer: No media element found for play/pause');
      return;
    }

    console.log('MediaPlayer: Toggle play/pause, currently playing:', isPlaying);
    
    if (isPlaying) {
      media.pause();
    } else {
      media.play().catch(error => {
        console.error('Play failed:', error);
      });
    }
  }, [isPlaying]);

  const handleSeek = useCallback((event: Event, value: number | number[]) => {
    const media = mediaRef.current;
    if (!media || Array.isArray(value)) return;

    media.currentTime = value;
    setCurrentTime(value);
  }, []);

  const handleVolumeChangeLocal = useCallback((event: Event, value: number | number[]) => {
    const media = mediaRef.current;
    if (!media || Array.isArray(value)) return;

    media.volume = value;
    setVolume(value);
    if (value === 0) {
      setIsMuted(true);
      media.muted = true;
    } else if (isMuted) {
      setIsMuted(false);
      media.muted = false;
    }
  }, [isMuted]);

  const handleMuteToggle = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    const newMuted = !isMuted;
    media.muted = newMuted;
    setIsMuted(newMuted);
  }, [isMuted]);

  const handleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    try {
      if (!isFullscreen) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  }, [isFullscreen]);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Don't return early - we need to render the media element to start loading

  const MediaElement = isVideo ? 
    <video
      ref={mediaRef as React.RefObject<HTMLVideoElement>}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        cursor: 'pointer',
        display: 'block',
      }}
      controls={true}
      controlsList=""
      poster={poster}
      preload="auto"
      playsInline
      crossOrigin="anonymous"
      onClick={handlePlayPause}
    /> :
    <audio
      ref={mediaRef as React.RefObject<HTMLAudioElement>}
      style={{
        width: '100%',
        minHeight: '54px', // Ensure minimum height for controls
        display: 'block',
        outline: 'none',
        border: '2px solid rgba(0, 212, 170, 0.3)', // Visible border for debugging
        borderRadius: '8px',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      }}
      controls={true}
      controlsList=""
      preload="auto"
      crossOrigin="anonymous"
    />;

  return (
    <Box
      ref={containerRef}
      className={className}
      style={style}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: isVideo ? '#000' : 'transparent',
        minHeight: 0, // Allow flex container to shrink
      }}
    >
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
            zIndex: 10,
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Loading {isVideo ? 'video' : 'audio'}...
          </Typography>
        </Box>
      )}

      {isVideo ? (
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            position: 'relative',
            minHeight: 0, // Allow flex item to shrink below its content size
            overflow: 'hidden' // Prevent controls from overflowing
          }}
        >
          {MediaElement}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            p: 4,
            background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.05) 0%, rgba(0, 212, 170, 0.02) 100%)',
            borderRadius: 2,
          }}
        >
          {/* Audio Icon with Animated Background */}
          <Box
            sx={{
              position: 'relative',
              fontSize: '6rem',
              animation: isPlaying ? 'pulse 2s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.05)',
                  opacity: 0.8,
                },
              },
            }}
          >
            🎵
          </Box>

          {/* File Name and Metadata */}
          <Box sx={{ textAlign: 'center', maxWidth: '600px' }}>
            <Typography variant="h6" color="text.primary" gutterBottom fontWeight={500}>
              {metadata.fileName}
            </Typography>
            {metadata.fileSize && (
              <Typography variant="caption" color="text.secondary">
                {(metadata.fileSize / (1024 * 1024)).toFixed(2)} MB
                {duration > 0 && ` • ${formatTime(duration)}`}
              </Typography>
            )}
          </Box>

          {/* Audio Player Controls */}
          <Box sx={{
            width: '100%',
            maxWidth: '600px',
            minHeight: '54px', // Ensure space for controls
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {MediaElement}
          </Box>

          {/* Format Information */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {metadata.mimeType?.split('/')[1]?.toUpperCase() || 'AUDIO'} Format
          </Typography>
        </Box>
      )}

      {/* Custom Controls */}
      {controls && showCustomControls && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mt: isVideo ? 1 : 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Progress Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: '40px' }}>
              {formatTime(currentTime)}
            </Typography>
            <Slider
              value={currentTime}
              max={duration}
              onChange={handleSeek}
              sx={{ flex: 1 }}
              size="small"
            />
            <Typography variant="caption" sx={{ minWidth: '40px' }}>
              {formatTime(duration)}
            </Typography>
          </Box>

          {/* Control Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePlayPause} size="small">
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <IconButton onClick={handleMuteToggle} size="small">
                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              <Slider
                value={isMuted ? 0 : volume}
                max={1}
                step={0.1}
                onChange={handleVolumeChangeLocal}
                sx={{ maxWidth: '100px' }}
                size="small"
              />
            </Box>

            {isVideo && (
              <Tooltip title="Fullscreen">
                <IconButton onClick={handleFullscreen} size="small">
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MediaPlayer;